import React from 'react';
import { NavLink } from 'react-router-dom';
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Typography,
  Avatar,
  IconButton,
  Tooltip,
  Chip,
} from '@mui/material';
import {
  Dashboard,
  People,
  Payment,
  MonetizationOn,
  Event,
  Logout,
  Settings as SettingsIcon,
  Person,
  ChevronLeft,
  ChevronRight,
  AccountBalance,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { selectSidebarCollapsed, setSidebarCollapsed } from '../../store/slices/uiSlice';
import { logoutUser } from '../../store/slices/authSlice';
import { persistor } from '../../store';
import logoImage from '../assests/cariya-logo.jpeg';

const PartnerSidebar = ({ partnerId }) => {
  const theme = useTheme();

  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  
  // Get sidebar state from Redux
  const isCollapsed = useAppSelector(selectSidebarCollapsed);
  
  // Get user data from Redux
  const user = useAppSelector((state) => state.auth.user);
  const partner_name = user?.name || 'Partner';

  const handleLogout = async () => {
    try {
      await dispatch(logoutUser()).unwrap();
      // Purge persisted state to ensure clean logout
      await persistor.purge();
      navigate('/login');
    } catch (error) {
      console.error('Logout failed:', error);
      // Still navigate to login even if logout fails
      navigate('/login');
    }
  };

  const handleToggleSidebar = () => {
    dispatch(setSidebarCollapsed(!isCollapsed));
  };

  // Main navigation (top)
  const navItems = [
    { 
      text: 'Dashboard', 
      icon: <Dashboard />, 
      path: `/partner/${partnerId}`,
      badge: null
    },
    { 
      text: 'Mothers', 
      icon: <People />, 
      path: `/partner/${partnerId}/mothers`,
      badge: null
    },
    { 
      text: 'Funding', 
      icon: <MonetizationOn />, 
      path: `/partner/${partnerId}/match-funding`,
      badge: null
    },
    { 
      text: 'Disbursements', 
      icon: <AccountBalance />, 
      path: `/partner/${partnerId}/contributions`,
      badge: null
    },
    { 
      text: 'Activities', 
      icon: <Event />, 
      path: `/partner/${partnerId}/activities`,
      badge: null
    },
  ];

  // Bottom navigation (bottom)
  const bottomNavItems = [
    { 
      text: 'Profile', 
      icon: <Person />, 
      path: `/partner/${partnerId}/profile`,
      badge: null
    },
    { 
      text: 'Subscription', 
      icon: <Payment />, 
      path: `/partner/${partnerId}/subscription`,
      badge: null
    },
    { 
      text: 'Settings', 
      icon: <SettingsIcon />, 
      path: `/partner/${partnerId}/settings`,
      badge: null
    },
  ];

  const renderNavItem = (item, index) => {
    const isActive = location.pathname === item.path;

    return (
      <Tooltip 
        key={item.text}
        title={isCollapsed ? item.text : ''} 
        placement="right"
        arrow
      >
        <ListItem disablePadding sx={{ mb: 0.5 }}>
          <ListItemButton
            component={NavLink}
            to={item.path}
            sx={{
              borderRadius: 2,
              mx: 1,
              py: 1.5,
              px: isCollapsed ? 1.5 : 2,
              transition: 'all 0.3s ease',
              backgroundColor: isActive ? 'rgba(249, 41, 85, 0.08)' : 'transparent',
              color: isActive ? '#f92955' : '#424242',
              '&:hover': {
                backgroundColor: isActive ? 'rgba(249, 41, 85, 0.12)' : 'rgba(249, 41, 85, 0.04)',
                transform: isCollapsed ? 'none' : 'translateX(4px)',
              },
              '&.active': {
                backgroundColor: 'rgba(249, 41, 85, 0.08)',
                color: '#f92955',
              },
              justifyContent: isCollapsed ? 'center' : 'flex-start',
              position: 'relative',
              minHeight: 48,
            }}
          >
            <ListItemIcon 
              sx={{ 
                color: isActive ? '#f92955' : '#f92955',
                minWidth: isCollapsed ? 0 : 40,
                mr: isCollapsed ? 0 : 2,
                transition: 'color 0.3s ease',
              }}
            >
              {item.icon}
            </ListItemIcon>
            
            {!isCollapsed && (
              <>
                <ListItemText
                  primary={item.text}
                  primaryTypographyProps={{
                    fontWeight: isActive ? 700 : 600,
                    fontSize: '0.9375rem',
                    letterSpacing: '0.2px',
                  }}
                />
                {item.badge && (
                  <Chip
                    label={item.badge}
                    size="small"
                    sx={{
                      height: 20,
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      backgroundColor: item.badgeColor || '#f92955',
                      color: 'white',
                      '& .MuiChip-label': {
                        px: 1,
                      },
                    }}
                  />
                )}
              </>
            )}

            {/* Active indicator */}
            {isActive && !isCollapsed && (
              <Box
                sx={{
                  position: 'absolute',
                  left: 0,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  width: 4,
                  height: '60%',
                  backgroundColor: '#f92955',
                  borderRadius: '0 2px 2px 0',
                }}
              />
            )}
          </ListItemButton>
        </ListItem>
      </Tooltip>
    );
  };

  return (
    <Box
      sx={{
        width: isCollapsed ? 80 : 280,
        bgcolor: '#FFFFFF',
        height: '100vh',
        boxShadow: '4px 0 20px rgba(0,0,0,0.08)',
        display: 'flex',
        flexDirection: 'column',
        borderRight: '1px solid rgba(0,0,0,0.08)',
        position: 'fixed',
        left: 0,
        top: 0,
        zIndex: theme.zIndex.drawer,
        transition: 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        overflow: 'hidden',
      }}
    >
      {/* Header with Logo and Toggle */}
      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        py: 3,
        px: 2,
        background: 'linear-gradient(135deg, #f92955 0%, #d41e3f 100%)',
        position: 'relative',
        overflow: 'hidden',
      }}>
        {/* Background Pattern */}
        <Box sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'radial-gradient(circle at 20% 80%, rgba(255,255,255,0.1) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(255,255,255,0.1) 0%, transparent 50%)',
        }} />
        
        <Avatar
          src={logoImage}
          alt="Cariya Wallet Logo"
          sx={{
            width: isCollapsed ? 48 : 64,
            height: isCollapsed ? 48 : 64,
            mb: isCollapsed ? 1 : 2,
            boxShadow: '0 4px 16px rgba(0,0,0,0.2)',
            border: '3px solid rgba(255,255,255,0.3)',
            backgroundColor: 'rgba(255,255,255,0.9)',
            transition: 'all 0.3s ease',
          }}
        />
        
        {!isCollapsed && (
          <>
            <Typography
              variant="h6"
              sx={{
                color: 'white',
                fontWeight: 800,
                letterSpacing: '1px',
                fontFamily: 'Inter, sans-serif',
                mb: 0.5,
                textShadow: '0 2px 8px rgba(0,0,0,0.2)',
                textAlign: 'center',
              }}
            >
              Cariya Wallet
            </Typography>
            {partner_name && (
              <Typography
                variant="caption"
                sx={{
                  color: 'rgba(255,255,255,0.9)',
                  fontStyle: 'italic',
                  fontWeight: 500,
                  textAlign: 'center',
                  maxWidth: 200,
                  opacity: 0.9,
                  fontSize: '0.75rem',
                }}
              >
                {partner_name}
              </Typography>
            )}
          </>
        )}
      </Box>

      {/* Toggle Button */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        py: 1,
        borderBottom: '1px solid rgba(0,0,0,0.06)',
      }}>
        <Tooltip title={isCollapsed ? "Expand Sidebar" : "Collapse Sidebar"} placement="right" arrow>
          <IconButton
            onClick={handleToggleSidebar}
            sx={{
                          color: '#f92955',
            backgroundColor: 'rgba(249, 41, 85, 0.04)',
            '&:hover': {
              backgroundColor: 'rgba(249, 41, 85, 0.08)',
                transform: 'scale(1.05)',
              },
              transition: 'all 0.2s ease',
            }}
          >
            {isCollapsed ? <ChevronRight /> : <ChevronLeft />}
          </IconButton>
        </Tooltip>
      </Box>

      {/* Main Navigation */}
      <Box sx={{ flexGrow: 1, overflowY: 'auto', py: 2 }}>
        <List>
          {navItems.map((item, index) => renderNavItem(item, index))}
        </List>
      </Box>

      {/* Bottom Navigation */}
      <Box sx={{ width: '100%', py: 2 }}>
        <Divider sx={{ mx: 2, mb: 2, borderColor: 'rgba(0,0,0,0.08)' }} />
        <List>
          {bottomNavItems.map((item, index) => renderNavItem(item, index))}
          
          {/* Logout Button */}
          <Tooltip title={isCollapsed ? "Logout" : ""} placement="right" arrow>
            <ListItem disablePadding sx={{ mb: 0.5 }}>
              <ListItemButton
                onClick={handleLogout}
                sx={{
                  borderRadius: 2,
                  mx: 1,
                  py: 1.5,
                  px: isCollapsed ? 1.5 : 2,
                  mt: 0.5,
                  color: '#f92955',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    backgroundColor: 'rgba(249, 41, 85, 0.08)',
                    transform: isCollapsed ? 'none' : 'translateX(4px)',
                  },
                  justifyContent: isCollapsed ? 'center' : 'flex-start',
                  minHeight: 48,
                }}
              >
                <ListItemIcon 
                  sx={{ 
                    color: '#f92955',
                    minWidth: isCollapsed ? 0 : 40,
                    mr: isCollapsed ? 0 : 2,
                  }}
                >
                  <Logout />
                </ListItemIcon>
                {!isCollapsed && (
                  <ListItemText
                    primary="Logout"
                    primaryTypographyProps={{
                      fontWeight: 600,
                      fontSize: '0.9375rem',
                      letterSpacing: '0.2px',
                    }}
                  />
                )}
              </ListItemButton>
            </ListItem>
          </Tooltip>
        </List>
      </Box>

      {/* Footer */}
      {!isCollapsed && (
        <Box sx={{ 
          p: 2, 
          borderTop: '1px solid rgba(0,0,0,0.06)',
          textAlign: 'center',
        }}>
          <Typography 
            variant="caption" 
            sx={{ 
              color: '#757575',
              fontSize: '0.75rem',
              fontWeight: 500,
            }}
          >
            © {new Date().getFullYear()} Cariya Wallet
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default PartnerSidebar;